'use client'

import { m, useInView, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useSyncExternalStore } from 'react'

interface Props {
    character: string
    strokePaths: string[]
}

// Device-local "already enacted" flag — the sanctioned localStorage carve-out
// (cf. `kanjido.printing.seen` in printing-notice, `hasAlreadySignedIn` in the
// signin header). Read through useSyncExternalStore so SSR and the first client
// render agree — the server snapshot reports `enacted`, so first paint is always
// the settled glyph (identical to the no-JS fallback) and the animation can only
// begin after hydration, once, ever. This also keeps the read out of an effect,
// avoiding react-hooks/set-state-in-effect.
const SEEN_KEY = 'kanjido.hero.enacted'

function subscribeSeen(callback: () => void) {
    const onStorage = (e: StorageEvent) => {
        if (e.key === SEEN_KEY) callback()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
}

function getSeenSnapshot() {
    return window.localStorage.getItem(SEEN_KEY) === '1'
}

function getSeenServerSnapshot() {
    return true
}

// Timeline (seconds). Each stroke draws for DRAW, offset by STAGGER; after the
// last finishes, HOLD lets the drawn glyph register as its own beat; then a
// clean cross-dissolve to the printed glyph over RESOLVE — no spring, no scale
// (2026-07-16 critique synthesis; landing spec §5).
const DRAW = 0.3
const STAGGER = 0.17
const HOLD = 0.42
const RESOLVE = 0.45

// The specimen is always 田. KanjiVG draws it with wide margins inside a 0–109
// box (~65% fill), while the mincho glyph fills ~90% of its em — so left raw the
// two forms cross-dissolve with a visible size pop. This crop frames the strokes
// to fill the box like the type does, registering the drawn and set 田 (verified
// against the printed glyph at 140px). The stroke weight is in viewBox units so
// it scales with the glyph — deliberately NOT `vector-effect: non-scaling-stroke`,
// which breaks the pathLength/stroke-dasharray normalization and scatters the
// strokes on every partial (mid-draw) frame.
const GLYPH_FRAME = '15 16 78 78'

/**
 * The Hero specimen's headline glyph, which enacts production: the kanji draws
 * itself from its strokes, holds, then cross-dissolves into the printed mincho
 * form — "drawn, then set in type."
 *
 * The printed glyph is the resting state and the reduced-motion / SSR / no-JS /
 * already-enacted fallback; it never unmounts. The stroke overlay mounts only
 * for the one-time enactment.
 */
export function SpecimenEnactment({ character, strokePaths }: Props) {
    const ref = useRef<HTMLDivElement | null>(null)
    const reduce = useReducedMotion()
    const inView = useInView(ref, { once: true, margin: '0px 0px -12% 0px' })
    const enacted = useSyncExternalStore(
        subscribeSeen,
        getSeenSnapshot,
        getSeenServerSnapshot
    )

    const play =
        inView && reduce === false && !enacted && strokePaths.length > 0

    useEffect(() => {
        if (play) window.localStorage.setItem(SEEN_KEY, '1')
    }, [play])

    const strokesDone = (strokePaths.length - 1) * STAGGER + DRAW
    const resolveAt = strokesDone + HOLD
    const total = resolveAt + RESOLVE
    const times = [0, resolveAt / total, 1]

    return (
        <div ref={ref} className="relative">
            {/* Printed mincho glyph — resting state + every fallback. During the
                enactment it stays hidden until the cross-dissolve. */}
            <m.div
                className="font-mincho text-[120px] leading-[0.85] text-ink select-none md:text-[140px]"
                animate={{ opacity: play ? [0, 0, 1] : 1 }}
                transition={
                    play
                        ? { duration: total, times, ease: 'easeInOut' }
                        : { duration: 0 }
                }
            >
                {character}
            </m.div>

            {/* Ink strokes — mounted only while enacting; drawn in canonical
                order (opacity-gated so no cap shows before its stroke draws),
                then cross-dissolved out as the printed glyph fades in. */}
            {play && (
                <m.svg
                    viewBox={GLYPH_FRAME}
                    aria-hidden
                    className="pointer-events-none absolute inset-0 size-full text-ink"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    animate={{ opacity: [1, 1, 0] }}
                    transition={{ duration: total, times, ease: 'easeInOut' }}
                >
                    {strokePaths.map((d, i) => (
                        <m.path
                            key={i}
                            d={d}
                            pathLength={1}
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{
                                pathLength: {
                                    duration: DRAW,
                                    delay: i * STAGGER,
                                    ease: [0.33, 0, 0.2, 1],
                                },
                                opacity: {
                                    duration: 0.001,
                                    delay: i * STAGGER,
                                },
                            }}
                        />
                    ))}
                </m.svg>
            )}
        </div>
    )
}
